<template>
	<div class="ddd-header">
		<!-- Left: avatar + title -->
		<div class="ddd-header-left">
			<div class="ddd-avatar">
				<v-icon size="15" color="white">mdi-bug</v-icon>
			</div>
			<div>
				<h2 class="ddd-title">{{ t('planning_driver_debug_title') }}</h2>
				<span class="ddd-subtitle">{{ props.driverDisplayName }}</span>
			</div>
		</div>

		<!-- Right: pills + violations + close -->
		<div class="ddd-header-right">
			<div class="ddd-pill ddd-pill--blue">
				<span class="ddd-pill-label">{{ t('planning_driver_debug_score') }}</span>
				<span class="ddd-pill-value">{{ formatToTwoDecimals(props.totalScore) }}</span>
			</div>

			<div class="ddd-pill ddd-pill--green">
				<span class="ddd-pill-label">{{ t('planning_driver_debug_averageWorkTime') }}</span>
				<span class="ddd-pill-value">
					<span class="ddd-pill-sub">
						{{ safeFormatSecondsAsTime(props.workTime) + ' (' + safeFormatSecondsAsTime(props.workTimeDifference) + ')' }}
					</span>
				</span>
			</div>

			<div class="ddd-pill ddd-pill--teal">
				<span class="ddd-pill-label">{{ t('planning_driver_debug_restDaysDifference') }}</span>
				<span class="ddd-pill-value">{{ formatToTwoDecimals(props.restDaysDifference) }}</span>
			</div>

			<v-chip v-if="props.violations > 0" size="small" color="error" variant="tonal" class="ddd-violations-chip">
				<v-icon size="13" start>mdi-alert-circle</v-icon>
				{{ props.violations }} {{ t('aria.violation') }}{{ props.violations !== 1 ? 's' : '' }}
			</v-chip>

			<button class="ddd-close-btn" @click="emit('close')" :title="t('action_close')">
				<v-icon size="20">mdi-close</v-icon>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatToTwoDecimals, safeFormatSecondsAsTime } from '@/rennovation/shared/utils/date-utils';

// ─────────────────────────────────────────────────────────────────────────────
// Props / emits
// ─────────────────────────────────────────────────────────────────────────────
const props = defineProps<{
	driverDisplayName: string;
	totalScore?: number;
	workTime?: number;
	workTimeDifference?: number;
	restDaysDifference?: number;
	violations?: number;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

// ─────────────────────────────────────────────────────────────────────────────
// i18n
// ─────────────────────────────────────────────────────────────────────────────
const { t } = useI18n();
</script>

<style scoped>
/* ── Header ──────────────────────────────────────────────────────────────── */
.ddd-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	background: var(--ddd-bg-head);
	border-bottom: 1px solid var(--ddd-border);
	flex-shrink: 0;
	min-height: var(--ddd-header-h);
}

.ddd-header-left {
	display: flex;
	align-items: center;
	gap: 10px;
}

.ddd-avatar {
	width: 30px;
	height: 30px;
	border-radius: 7px;
	background: var(--ddd-avatar-bg);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.ddd-title {
	font-family: var(--ddd-font-ui);
	font-size: 14px;
	font-weight: 700;
	color: var(--ddd-text-dark);
	margin: 0;
	line-height: 1.2;
}

.ddd-subtitle {
	font-family: var(--ddd-font-ui);
	font-size: 12px;
	color: var(--ddd-text-sub);
	display: block;
	margin-top: 1px;
}

.ddd-header-right {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

/* ── Pills ───────────────────────────────────────────────────────────────── */
.ddd-pill {
	display: flex;
	align-items: center;
	gap: 5px;
	border-radius: 6px;
	padding: 4px 11px;
	border: 1px solid;
}

.ddd-pill--blue {
	background: var(--ddd-pill-blue-bg);
	border-color: var(--ddd-pill-blue-border);
}

.ddd-pill--green {
	background: var(--ddd-pill-green-bg);
	border-color: var(--ddd-pill-green-border);
}

.ddd-pill--teal {
	background: var(--ddd-pill-teal-bg);
	border-color: var(--ddd-pill-teal-border);
}

.ddd-pill-label {
	font-family: var(--ddd-font-ui);
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: var(--ddd-pill-label);
}

.ddd-pill-value {
	font-family: var(--ddd-font-mono);
	font-size: 13px;
	font-weight: 700;
	color: var(--ddd-pill-value);
}

.ddd-pill-sub {
	font-size: 11px;
	font-weight: 500;
	color: var(--ddd-pill-sub);
	margin-left: 2px;
}

.ddd-violations-chip {
	font-size: 12px !important;
}

.ddd-close-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 6px;
	border: 1px solid var(--ddd-border);
	background: transparent;
	cursor: pointer;
	color: var(--ddd-text-sub);
	transition:
		background 0.15s,
		color 0.15s;
}

.ddd-close-btn:hover {
	background: var(--ddd-close-hover-bg);
	color: var(--ddd-close-hover-text);
	border-color: var(--ddd-close-hover-border);
}
</style>
